import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Box, CircularProgress, Typography } from "@mui/material";
import { toast } from "react-toastify";
import BossesTable from "../Components/BossesTable";
import SearchBar from "../Components/SearchBar";
import Pagination from "../Components/Pagination";
import DeleteModal from "../Components/modals/DeleteModal";
import { GetAllData } from "../NetworkCalls/Products/ServerReq";
import { apiUrl, axiosInstance } from "../axios";

const PageContainer = styled('div')(({ theme }) => ({
  padding: "16px",
  backgroundColor: "#f5f6fe",
  minHeight: "100vh",
  marginLeft: "40px",
  width: "calc(100% - 40px)",
  marginTop: "64px",

  '@media (max-width: 768px)': {
    marginRight: "auto",
    marginLeft: "auto",
    width: "100%",
    padding: "12px"
  }
}));

const Title = styled.h1`
  font-family: "Urbanist";
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 24px;
`;

export const EmployeesList = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pageData, setPageData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false); // New state to track if user is searching

  const handleClose = () => {
    setOpenModal(false);
  };

  const renderNameWithIcon = (item) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {item.username}
      {item.profileUrl && (
        <a href={item.profileUrl} target="_blank" rel="noopener noreferrer">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M5.92962 2.31053H2.2324C1.90555 2.31053 1.59208 2.44037 1.36096 2.67149C1.12984 2.90261 1 3.21608 1 3.54293V9.70496C1 10.0318 1.12984 10.3453 1.36096 10.5764C1.59208 10.8075 1.90555 10.9374 2.2324 10.9374H8.39443C8.72128 10.9374 9.03475 10.8075 9.26587 10.5764C9.49699 10.3453 9.62683 10.0318 9.62683 9.70496V6.00774M5.31342 6.62395L10.8592 1.07812M10.8592 1.07812H7.77823M10.8592 1.07812V4.15914" stroke="#181C23" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>
      )}
    </div>
  );

  const columns = [
    { key: "username", label: "Employee Name", renderer: (_, item) => renderNameWithIcon(item) },
    { key: "email", label: "Email" },
    { key: "jobTitle", label: "Job Title" },
    { key: "action", label: "Action" }
  ];

  useEffect(() => {
    const fetchData = async () => {
      const userId = JSON.parse(localStorage.getItem("info"))?.userId;
      const response = await GetAllData(`user/allUsers?userId=${userId}`);
      setData(response);
      setFilteredData(response);
      setLoader(false);
    };
    fetchData();
  }, []);

  const handleSearch = async (filters) => {
    const userId = JSON.parse(localStorage.getItem("info"))?.userId;
    const token = localStorage.getItem("token");

    // Prepare search parameters
    const searchParams = {
      text: filters.employeeName || undefined,
      jobTitle: filters.jobTitle || undefined
    };

    // Check if user is actually searching
    const hasSearchTerms = Boolean(searchParams.text || searchParams.jobTitle);
    setIsSearching(hasSearchTerms);

    // Reset to all data if all fields are empty
    if (!hasSearchTerms) {
      setFilteredData(data);
      return;
    }

    try {
      const response = await axiosInstance.post(
        `${apiUrl}user/search?userId=${userId}`,
        searchParams,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setFilteredData(response.data.data);
    } catch (error) {
      console.error("Employee search failed:", error);
      setFilteredData([]); // Show "No employees found"
    }
  };

  const handleDeleteRequest = (user) => {
    setDeleteId(user._id);
    setOpenModal(true);
  };

  const handleDelete = async () => {
    const userId = JSON.parse(localStorage.getItem("info"))?.userId;
    setDeleteLoading(true);
    try {
      await axiosInstance.delete(`${apiUrl}user/deleteUser/${deleteId}?userId=${userId}`);
      toast.success("Deleted Successfully");
      setFilteredData(prev => prev.filter(u => u._id !== deleteId));
      // Also update the main data array
      setData(prev => prev.filter(u => u._id !== deleteId));
    } catch (err) {
      toast.error("Delete Failed");
    } finally {
      setDeleteLoading(false);
      setOpenModal(false);
    }
  };

  // Helper function to determine what message to show
  const getNoDataMessage = () => {
    if (data.length === 0) {
      return "No employees found";
    }
    if (isSearching && filteredData.length === 0) {
      return "No matching results found";
    }
    return null;
  };

  const noDataMessage = getNoDataMessage();

  if (loader) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress color="inherit" sx={{ color: "#12188A" }} />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Title>Employees List</Title>
      <SearchBar
        fields={[
          { name: "employeeName", placeholder: "Employee Name" },
          { name: "jobTitle", placeholder: "Job Title" }
        ]}
        onSearch={handleSearch}
        buttonText="Search"
        buttonWidth="240px"
      />
      
      {noDataMessage ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <Typography style={{ color: '#64748B', fontSize: '14px' }}>
            {noDataMessage}
          </Typography>
        </Box>
      ) : (
        <>
          <div style={{ marginTop: "24px" }}>
            <BossesTable
              data={pageData}
              columns={columns}
              onActionClick={handleDeleteRequest}
              actionLabel="Delete"
              showAvatar={false}
            />
          </div>
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Pagination data={filteredData} setPageData={setPageData} itemsPerPage={10} />
          </Box>
        </>
      )}
      
      <DeleteModal
        open={openModal}
        onClose={handleClose}
        onDelete={handleDelete}
        title="Are You Sure You Want to Remove This Employee?"
        description="This will permanently remove the employee from the platform and delete any recommendations they've submitted. This action cannot be undone."
        deleteButtonText="Delete Employee"
        isDeleting={deleteLoading}
      />
    </PageContainer>
  );
};

export default EmployeesList;